import {
  IAnalyticsMetric,
  MetricValueKind,
} from './analytics-metric.interface.js';
import {
  MetricCapability as Cap,
  MetricInputKey as Input,
  MetricRole as Role,
  MetricSolution as Sol,
  MetricTier,
} from './metric-taxonomy.js';

/**
 * THE metric catalogue. One definition, three consumers: the platform's per-role
 * analytics tabs, the marketing site's solution pages, and its role pages.
 *
 * A constant rather than a table on purpose. These change at the speed of product
 * decisions, not user actions, and a typed constant gets compile-time safety on
 * `tier` and `inputs` — the two fields whose drift would put a fabricated number
 * on screen. Same reasoning as `docs/glossary.json` (working-conventions §44).
 *
 * AVAILABILITY DISCIPLINE, which is the point of the whole file. Only ONE fact is
 * stored — `blocked` — because only one is independent:
 *   blocked         — never renders a number. The events do not exist. A placeholder
 *                     is honest; a computed-looking figure is not.
 *   inputs, unset   — renders a click-to-set empty state, never a silent default.
 *   inputs, set     — renders.
 *   no inputs       — renders from recorded facts alone.
 *
 * `metricTier()` collapses that into the A/B/C label for reporting. It is DERIVED.
 * Storing it alongside `inputs` (as an earlier version did) meant two fields encoded
 * one fact and a test existed purely to stop the copies disagreeing.
 */
export const METRIC_REGISTRY: IAnalyticsMetric[] = [
  // ───────────────────────── Value metrics (roll into the ROI hero) ─────────
  {
    id: 'stolen_recovery_value',
    title: 'Stolen Recovery Value',
    unit: 'recovered',
    description:
      'Value of product recorded as lost or stolen that was subsequently recovered and returned to an active certificate.',
    solutions: [Sol.BrandProtection, Sol.TraceabilityLogistics],
    roles: [Role.Legal, Role.Logistics, Role.Finance, Role.Admin],
    evidences: [Cap.StolenProductPrevention, Cap.BrandProtectionSoftware],
    inputs: [],
    valueKind: MetricValueKind.Currency,
    contributesToRoi: true,
    formula:
      'Sum of product price over certificates that recorded a lost/stolen event and later returned to active.',
  },
  {
    id: 'resale_value_captured',
    title: 'Resale Value Captured',
    unit: 'in tracked resale',
    description:
      'Total declared value of completed ownership transfers, i.e. resale that Vera made visible to the brand.',
    solutions: [Sol.ResaleCircularity],
    roles: [Role.Finance, Role.Marketing, Role.Admin],
    evidences: [Cap.ResaleMonetization, Cap.OwnerUpsellOnResale],
    inputs: [],
    valueKind: MetricValueKind.Currency,
    contributesToRoi: true,
    formula: 'Sum of transfer price over transfers completed in the period.',
  },

  // ───────────────────────── Currency, but NOT return delivered ─────────────
  {
    id: 'certified_catalogue_value',
    title: 'Certified Catalogue Value',
    unit: 'under certificate',
    description:
      'Total value of product currently carrying an active Vera certificate.',
    solutions: [Sol.BrandProtection, Sol.TraceabilityLogistics],
    roles: [Role.Finance, Role.Purchasing, Role.Admin],
    evidences: [Cap.BrandProtectionSoftware, Cap.CpaAuthentication],
    inputs: [],
    valueKind: MetricValueKind.Currency,
    // Exposure covered, not return delivered. Adding it to the ROI numerator
    // would count the whole catalogue as a benefit Vera produced.
    contributesToRoi: false,
    formula: 'Sum of product price over certificates currently active.',
  },
  {
    id: 'vera_spend',
    title: 'Vera Spend',
    unit: 'this period',
    description:
      'What this company has paid Vera in the period: subscription plus billable activations.',
    solutions: [],
    roles: [Role.Finance, Role.Admin],
    evidences: [],
    inputs: [],
    valueKind: MetricValueKind.Currency,
    // This IS the ROI denominator. Adding it to the numerator would be circular.
    contributesToRoi: false,
    formula:
      'Subscription amount for the billing periods in range, plus activations beyond the included allowance priced at the plan tier.',
  },

  // ───────────────────────── Rates and counts ────────────────────────────────
  {
    id: 'consumer_touchpoints',
    title: 'Consumer Touchpoints',
    unit: 'total scans',
    description: 'Total scans recorded across the selected period.',
    solutions: [Sol.ConsumerEngagement, Sol.AiAnalytics],
    roles: [Role.Marketing, Role.Analytics, Role.Admin],
    evidences: [Cap.MarketDemandAnalysis, Cap.InfluencerIdentification],
    inputs: [],
    valueKind: MetricValueKind.Count,
    contributesToRoi: false,
    formula: "Count of certificate action logs of type 'scan' in range.",
  },
  {
    id: 'repeat_scan_rate',
    title: 'Repeat Scan Rate',
    unit: 'of scanned items',
    description: 'Share of scanned items that were scanned more than once.',
    solutions: [Sol.ConsumerEngagement],
    roles: [Role.Marketing, Role.Analytics],
    evidences: [Cap.MarketDemandAnalysis],
    inputs: [],
    valueKind: MetricValueKind.Percentage,
    contributesToRoi: false,
    formula:
      'Certificates scanned 2+ times divided by certificates scanned at all. Always shown with its denominator.',
  },
  {
    id: 'geographic_reach',
    title: 'Geographic Reach',
    unit: 'countries',
    description:
      'Distinct countries where scans were recorded, from scans carrying a resolved country.',
    solutions: [Sol.ConsumerEngagement, Sol.TraceabilityLogistics],
    roles: [Role.Marketing, Role.Analytics, Role.Logistics],
    evidences: [Cap.MarketDemandAnalysis, Cap.SupplyChainMonitoring],
    inputs: [],
    valueKind: MetricValueKind.Count,
    contributesToRoi: false,
    formula:
      'Distinct resolved countries across scans in range. Located scans without a resolved country are reported separately rather than dropped.',
  },
  {
    id: 'authenticity_verification_rate',
    title: 'Authenticity Rate',
    unit: 'of scans verified',
    description:
      'Share of scans that confirmed the product as genuine via its Vera certificate.',
    solutions: [Sol.BrandProtection],
    roles: [Role.Legal, Role.Analytics, Role.Admin],
    evidences: [Cap.CpaAuthentication, Cap.CounterfeitMarketAnalysis],
    inputs: [],
    valueKind: MetricValueKind.Percentage,
    contributesToRoi: false,
    formula: 'Verified scans divided by all scans in range.',
  },
  {
    id: 'transfer_completion_rate',
    title: 'Transfer Completion',
    unit: 'of initiated transfers',
    description:
      'Share of ownership transfer requests that completed successfully.',
    solutions: [Sol.ResaleCircularity],
    roles: [Role.Legal, Role.Logistics, Role.Analytics],
    evidences: [Cap.ResaleCustomerCapture, Cap.CpaAdministration],
    inputs: [],
    valueKind: MetricValueKind.Percentage,
    contributesToRoi: false,
    formula:
      'Completed transfers divided by all transfers initiated in range. Both figures are already returned by the totals endpoint.',
  },
  {
    id: 'certificate_coverage',
    title: 'Certificate Coverage',
    unit: 'of products certified',
    description:
      'Share of catalogue products that carry at least one Vera certificate.',
    solutions: [Sol.BrandProtection, Sol.SustainabilityCompliance],
    roles: [Role.Purchasing, Role.Legal, Role.Admin],
    evidences: [Cap.DppCompliance, Cap.BrandProtectionSoftware],
    inputs: [],
    valueKind: MetricValueKind.Percentage,
    contributesToRoi: false,
    formula: 'Products with 1+ certificate divided by all products.',
  },
  {
    id: 'in_transit_rate',
    title: 'In-Transit Rate',
    unit: 'of certificates',
    description:
      'Share of certificates currently sitting in an in-progress transfer state.',
    solutions: [Sol.TraceabilityLogistics],
    roles: [Role.Logistics, Role.Analytics],
    evidences: [Cap.SupplyChainMonitoring, Cap.InventoryManagement],
    inputs: [],
    valueKind: MetricValueKind.Percentage,
    contributesToRoi: false,
    formula: 'Certificates in a transfer status divided by all certificates.',
  },
  {
    id: 'scan_location_accuracy',
    title: 'Location Resolution',
    unit: 'of scans geo-resolved',
    description:
      'Share of scans where a geographic location was successfully resolved. A data-quality measure, not a value measure.',
    solutions: [Sol.TraceabilityLogistics, Sol.AiAnalytics],
    roles: [Role.Logistics, Role.Engineering, Role.Analytics],
    evidences: [Cap.SupplyChainMonitoring],
    inputs: [],
    valueKind: MetricValueKind.Percentage,
    contributesToRoi: false,
    formula: 'Scans with resolved coordinates divided by all scans in range.',
  },
  {
    id: 'resale_customer_capture',
    title: 'Resale Customers',
    unit: 'new owners captured',
    description:
      'Owners newly acquired through a resale transfer who were not previously known to the brand.',
    solutions: [Sol.ResaleCircularity, Sol.ConsumerEngagement],
    roles: [Role.Marketing, Role.Analytics],
    evidences: [Cap.ResaleCustomerCapture, Cap.OwnerUpsellOnResale],
    inputs: [],
    valueKind: MetricValueKind.Count,
    contributesToRoi: false,
    formula:
      'Distinct transfer recipients in range with no prior ownership of this brand.',
  },

  // ───────────────────────── Need a brand-supplied assumption ───────────────
  {
    id: 'counterfeit_deflection_value',
    title: 'Counterfeit Deflection',
    unit: 'value protected',
    description:
      'Estimated value protected by verification failures that represent a genuine counterfeit encounter.',
    solutions: [Sol.BrandProtection],
    roles: [Role.Legal, Role.Finance, Role.Admin],
    evidences: [
      Cap.CounterfeitMarketAnalysis,
      Cap.CounterfeitLitigation,
      Cap.MarketplaceListingTakedowns,
    ],
    inputs: [Input.CounterfeitConversionRate, Input.GrossMarginRate],
    valueKind: MetricValueKind.Currency,
    contributesToRoi: true,
    formula:
      'Invalid scans x product price x your counterfeit rate x your gross margin. Rests on the counterfeit rate you supply; it is an estimate, not a measurement.',
  },
  {
    id: 'new_customer_acquisition_value',
    title: 'Customer Acquisition',
    unit: 'acquisition value',
    description:
      'Value of customers captured through certificate scans, priced at your own acquisition cost.',
    solutions: [Sol.ConsumerEngagement, Sol.ResaleCircularity],
    roles: [Role.Marketing, Role.Finance],
    evidences: [Cap.ResaleCustomerCapture, Cap.NewProductSales],
    inputs: [Input.CustomerAcquisitionCost],
    valueKind: MetricValueKind.Currency,
    contributesToRoi: true,
    formula:
      'Leads captured x your customer acquisition cost. The lead count is measured; the per-customer value is yours.',
  },

  // ───────────────────────── Blocked — the events do not exist yet ──────────
  {
    id: 'return_fraud_value_recovered',
    title: 'Return Fraud Prevented',
    unit: 'value retained',
    description:
      'Value retained by rejecting fraudulent returns using tag and ownership verification on returns.',
    solutions: [Sol.BrandProtection, Sol.TraceabilityLogistics],
    roles: [Role.Legal, Role.Logistics, Role.Finance],
    evidences: [Cap.ReturnFraudElimination],
    inputs: [Input.ReturnFraudAnnualLoss],
    valueKind: MetricValueKind.Currency,
    contributesToRoi: true,
    // NOT blocked, and this needs no return-event instrumentation. Vera's provenance
    // check is deterministic: a certified item either verifies as the one that was
    // sold, or it does not. So the figure is not an estimate of DETECTION -- it is the
    // brand's own stated exposure, which Vera then eliminates.
    //
    // Scaled by certificate coverage on purpose. Elimination is 100% of COVERED items,
    // not 100% of returns: nothing was there to verify on uncertified stock, and a
    // brand will notice if we claim to have stopped fraud on goods we never touched.
    formula:
      'Your stated annual return-fraud loss, prorated to the period, multiplied by certificate coverage. Vera catches all of it on covered items; uncovered stock is unchanged.',
  },
  {
    id: 'campaign_roi',
    title: 'Campaign ROI',
    unit: 'return per spend',
    description:
      'Return on marketing investment attributable to certificate-linked scan activity.',
    solutions: [Sol.ConsumerEngagement, Sol.AiAnalytics],
    roles: [Role.Marketing, Role.Finance],
    evidences: [Cap.InfluencerIdentification, Cap.SalespersonEngagementAnalysis],
    blocked: {
      reason:
        'Needs marketing spend and lead-to-customer attribution. Neither is captured anywhere in the system.',
    },
    inputs: [],
    valueKind: MetricValueKind.Currency,
    contributesToRoi: true,
    formula:
      'Marketing spend attributable to certificate-linked scan activity, against revenue from it.',
  },
  {
    id: 'scan_engagement_rate',
    title: 'Scan Engagement',
    unit: 'of units scanned',
    description:
      'Share of product units that received at least one consumer scan.',
    solutions: [Sol.ConsumerEngagement],
    roles: [Role.Marketing, Role.Analytics],
    evidences: [Cap.MarketDemandAnalysis, Cap.NewProductSales],
    blocked: {
      reason:
        'No units-produced figure exists. Supplying one per product per period would unblock it; counting over certificates instead answers a different question and is a separate metric, not this one renamed.',
    },
    inputs: [],
    valueKind: MetricValueKind.Percentage,
    contributesToRoi: false,
    // Deliberately NOT quietly redefined over certificates. A brand making 10,000
    // units and issuing 500 certificates reads 80% one way and 4% the other; those
    // answer different questions and only one belongs in front of a marketing lead.
    formula:
      'Units that received at least one consumer scan, over units produced.',
  },
];

/**
 * The A/B/C label, derived. Never store this — see `MetricTier`.
 *
 * Order matters: `blocked` wins over `inputs`. Return fraud was briefly both, and
 * supplying an assumption would not have helped, because there was nothing to apply
 * it to. A metric that is blocked is blocked regardless of what the brand tells us.
 */
export const metricTier = (m: IAnalyticsMetric): MetricTier => {
  if (m.blocked) return MetricTier.SourceNotInstrumented;
  return m.inputs.length > 0 ? MetricTier.NeedsInput : MetricTier.Derived;
};

/** True when the metric can render a figure given the inputs the brand has set. */
export const canRenderValue = (
  m: IAnalyticsMetric,
  setInputs: ReadonlySet<Input>,
): boolean => !m.blocked && m.inputs.every(i => setInputs.has(i));

/** Lookup by id. Ids are stable and linked to from the marketing site. */
export const METRIC_BY_ID: Record<string, IAnalyticsMetric> =
  METRIC_REGISTRY.reduce<Record<string, IAnalyticsMetric>>((acc, m) => {
    acc[m.id] = m;
    return acc;
  }, {});

/** Metrics for one company role's analytics tab, registry order preserved. */
export const metricsForRole = (role: Role): IAnalyticsMetric[] =>
  METRIC_REGISTRY.filter(m => m.roles.includes(role));

/** Metrics evidencing one solution — the marketing site's solution pages. */
export const metricsForSolution = (solution: Sol): IAnalyticsMetric[] =>
  METRIC_REGISTRY.filter(m => m.solutions.includes(solution));

/** Metrics evidencing one capability. */
export const metricsForCapability = (capability: Cap): IAnalyticsMetric[] =>
  METRIC_REGISTRY.filter(m => m.evidences.includes(capability));

/**
 * The metrics whose values sum into the ROI hero's numerator.
 *
 * Currency + contributesToRoi, and nothing else. The pairing is asserted here
 * rather than trusted: a non-currency metric marked as contributing would add a
 * percentage to a pile of dollars.
 */
export const roiContributingMetrics = (): IAnalyticsMetric[] =>
  METRIC_REGISTRY.filter(
    m => m.contributesToRoi && m.valueKind === MetricValueKind.Currency,
  );

/** Brand inputs a role's tab will ask for, deduplicated. */
export const inputsRequiredForRole = (role: Role): Input[] => [
  ...new Set(metricsForRole(role).flatMap(m => m.inputs)),
];
